import { Button } from "../Button";
import classNames from "classnames";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import TextTooltip from "../TextTooltip";
import { postAccess } from "@const/postManagement";
import { removeEmptyKeys } from "@helpers/common";

import { localstorageKeys } from "@const/store";
import { useService } from "@/base/hooks/useService";
import { StorageService } from "@services/StorageService";
import { toggleSignInModal } from "@redux/signIn/actions";
import routes from "@const/routes";

import styles from "./index.module.scss";

const labelStyle =
  "mb-3 mb-xl-4 text-body-2 subtitle-2-xl text-medium text-break text-formatted text-center";

const LockedPostsButtons = ({
  id,
  toggleFollow,
  canBePayed = true,
  accessType,
  username,
  title,
  modalView,
  uniqueKey,
  accessToken,
  isInSubscription,
}) => {
  const { t } = useTranslation("modals");
  const { t: t1 } = useTranslation("buttons");
  const { t: t2 } = useTranslation("common");
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { pathname, search } = useLocation();
  const storageService = useService(StorageService);
  const searchParams = new URLSearchParams(search);

  const { isAvailableByOneTimePayment, isAvailableBySubscription } =
    useSelector(
      ({
        postsReducer,
        postDetailsReducer,
        profileReducer,
        creatorProfileReducer,
      }) => {
        if (isInSubscription) return postDetailsReducer;
        return (
          creatorProfileReducer.postById?.[id] ||
          profileReducer.postById?.[id] ||
          postsReducer.postById?.[id] ||
          postDetailsReducer
        );
      },
    ) || {};

  const tooltipText = t2("NO_METHOD_CONNECTED", { username });

  const goToAuth = (nextPath, query) => {
    storageService.set(localstorageKeys.destination, `${nextPath}?${query}`);
    dispatch(toggleSignInModal(true));
  };

  const handleClick = () => {
    let nextPath;
    let query;

    if (isAvailableByOneTimePayment && !isAvailableBySubscription) {
      query = new URLSearchParams(
        removeEmptyKeys({
          u: username,
          p: uniqueKey,
          sub: searchParams.get("sub"),
        }),
      );

      nextPath = "/purchase-post";
    }

    if (isAvailableBySubscription) {
      nextPath = `${routes.PURCHASE_SUBSCRIPTION_BY_POST(true)}${id}`;
      query = new URLSearchParams(
        removeEmptyKeys({ sub: searchParams.get("sub"), p: uniqueKey }),
      );
    }

    if (!accessToken) {
      goToAuth(nextPath, query);
    } else {
      const stringifiedQuery = query.toString();
      navigate(`${nextPath}?${stringifiedQuery}`, {
        pathname: nextPath,
        search: stringifiedQuery,
        state: { from: pathname + search },
        viewTransition: true,
      });
    }
  };

  const text =
    isAvailableByOneTimePayment && isAvailableBySubscription
      ? "GET_BY_PURCHASING_OR_SUBSCRIBE"
      : isAvailableBySubscription
        ? "GET_BY_SUBSCRIBE"
        : isAvailableByOneTimePayment
          ? "GET_BY_PURCHASING"
          : ""; // all post subs are deleted

  return (
    <div
      className={classNames(
        "position-absolute translate-middle top-50 start-50",
        styles.container,
      )}
    >
      {modalView && (
        <div
          className={classNames(
            "text-body-1 subtitle-1-xl text-semibold text-center text-break",
            styles.title,
          )}
        >
          {title}
        </div>
      )}
      {accessType == postAccess.paid ? (
        <div className="w-100">
          <div className="text-center">
            <div className={labelStyle}>{text ? t(text) : text}</div>
            <TextTooltip
              text={tooltipText}
              wrapperClassName={classNames("mx-auto", styles.buttonWrapper)}
              tooltipClassName={classNames(
                "text-body-2 text-regular",
                styles.tooltip,
                canBePayed && "d-none",
              )}
            >
              <Button
                size="xs"
                onClick={handleClick}
                disabled={
                  !isAvailableByOneTimePayment && !isAvailableBySubscription
                }
                color="primary"
                className={classNames("w-100 px-0 text-body-2")}
              >
                {t1("GET_ACCESS")}
              </Button>
            </TextTooltip>
          </div>
        </div>
      ) : (
        <div className="d-flex flex-column  align-items-center text-center">
          <div className="mb-3 mb-xl-4 text-body-2 text-body-1-xl text-medium text-break text-formatted">
            {t("GET_BY_FOLLOWING", { username })}
          </div>
          <div className={styles.buttonWrapper}>
            <Button
              onClick={toggleFollow}
              color="primary"
              size="sm"
              className={classNames("w-100 text-body-2")}
            >
              {t1("GET_ACCESS")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LockedPostsButtons;
